import type { PayoutStatementLineView, PayoutStatementView } from '@/api/generated';
import { CSV_BOM, csvField, guardFormula, minorToDecimal } from '@/utils/csv';
import { isSyntheticAffiliateLine } from './statementMath';

const HEADER =
  'period_start,period_end,payment_ref,kind,line,collected_at,retail,wholesale,margin,currency,affiliate_commission';

function lineRow(stmt: PayoutStatementView, line: PayoutStatementLineView): string {
  const currency = stmt.currency;
  // A missing optional amount is an empty cell, never 0.00 (RPT-005). Required amounts are sent.
  const amount = (minor: number | null | undefined) =>
    currency && minor !== undefined && minor !== null ? minorToDecimal(minor, currency) : '';
  const synthetic = isSyntheticAffiliateLine(line);
  const cells = [
    guardFormula(stmt.periodStart),
    guardFormula(stmt.periodEnd),
    guardFormula(line.payApiPaymentRef ?? ''),
    guardFormula(line.kind),
    guardFormula(line.line ?? ''),
    guardFormula(line.collectedAt ?? ''),
    synthetic ? '' : amount(line.retailMinor),
    synthetic ? '' : amount(line.wholesaleMinor),
    synthetic ? '' : amount(line.resellerMarginMinor),
    guardFormula(currency),
    amount(line.affiliateCommissionMinor),
  ];
  return cells.map(csvField).join(',');
}

export function ledgerFilename(stmt: PayoutStatementView): string {
  return `tmm-reseller-ledger-${stmt.periodStart}-${stmt.periodEnd}.csv`;
}

/**
 * One settlement's ledger as CSV. Text cells carry 0028's formula guard; amounts are plain
 * decimals in the settlement's currency (every line on a reseller statement shares it — tmm-api,
 * 2026-09-25); a missing amount is an empty cell, never `0.00`. `margin` is the line's
 * `resellerMarginMinor` (what the customer paid less what we charged; may be negative) — the
 * reseller's money per line, less `affiliate_commission`. The retired revenue-share columns
 * (`uplift`, `reseller_share`, `platform_share`: 0 on every line since TMMAPI-0100) are not
 * exported (TMMRSL-0048), so the columns match the screen. A reseller has one tenant (MM-D-107),
 * so there is no tenant column (TMMRSL-0073).
 */
export function toCsv(stmt: PayoutStatementView): string {
  const rows = (stmt.lines ?? []).map((line) => lineRow(stmt, line));
  return `${CSV_BOM}${HEADER}\n${rows.join('\n')}${rows.length > 0 ? '\n' : ''}`;
}
